/**
 * 페이지별 필터 적용 범위 (DESIGN §8.6.8 레일 4구획).
 *
 * **왜 필요한가.** 페이지마다 실제로 타는 필터 축이 다르다. 지도는 기간을
 * 무시하고, 코호트는 사업자·제조사만 받으며, 품질·분석·대조는 아무 축도 안
 * 받는다. 지금까지는 사용자가 필터를 걸어도 화면이 안 바뀌는 이유를 알 수
 * 없었다 — 결측을 숨기지 않는다는 원칙(CLAUDE.md 절대원칙 2)은 "이 필터는
 * 여기 안 걸린다"에도 똑같이 적용된다.
 *
 * **근거는 `src/evca/warehouse/queries.sql` 이다.** 각 이름난 질의가 실제로
 * `$busi` · `$zcode` … 를 받는지 기계적으로 세어서 만들었다. SQL 이 바뀌면
 * 이 표가 조용히 거짓말을 하므로, 바꿀 때 함께 고쳐야 한다.
 * 대조 방법:
 *   grep -n '\$busi\|\$zcode\|\$maker\|\$from\|\$year\|\$min_coverage' \
 *     src/evca/warehouse/queries.sql
 */

/** 좌 레일이 노출하는 필터 축. `mode` 는 정의라 여기 없다 (§8.6.8 표) */
export type ScopeAxis =
  | 'busi' | 'zcode' | 'maker' | 'chgerType' | 'kind' | 'year' | 'floor' | 'minCoverage'

export const AXIS_LABEL: Record<ScopeAxis, string> = {
  busi: '사업자',
  zcode: '지역',
  maker: '제조사',
  chgerType: '충전기 타입',
  kind: '설치 환경',
  year: '설치 연식',
  floor: '설치 층',
  minCoverage: '최소 커버리지',
}

const ALL: ScopeAxis[] = [
  'busi', 'zcode', 'maker', 'chgerType', 'kind', 'year', 'floor', 'minCoverage']

/**
 * 적용되지 않는 축의 **성격**.
 *
 *   'n/a'         걸면 화면이 답하려는 질문이 성립하지 않는다 — 원래 그렇다
 *   'unsupported' 질문은 성립하는데 지금 구조가 못 한다 — 만들면 된다
 *
 * 둘을 같은 말로 쓰면 "원래 이런 건가, 덜 만든 건가"를 구분할 수 없다.
 */
export type OffKind = 'n/a' | 'unsupported'

interface Scope {
  /** 이 페이지의 질의가 실제로 받는 축 */
  applies: ScopeAxis[]
  /** 안 걸리는 축의 기본 성격 */
  offKind: OffKind
  /** 기본과 다른 축만 적는다 — 예외를 코드에 숨기지 않는다 */
  offBy?: Partial<Record<ScopeAxis, { kind: OffKind; reason: string }>>
  /**
   * **일부 위젯에만** 걸리는 축 (DESIGN §8.6.10).
   *
   * 개요의 `minCoverage` 가 그 예다 — 요약 KPI·분포에 걸리고 순위 질의에는
   * 걸리지 않는다. 「모두 적용」이라고 쓰면 거짓말이 되므로 셋으로 나눈다.
   */
  partial?: Partial<Record<ScopeAxis, string>>
  /** 안 받는 축의 사유. 숨기지 않고 회색 + 사유로 적는다 */
  reason: string
}

/**
 * 확인된 사실 (queries.sql 실측):
 *   metrics_* (개요)        busi zcode maker chger_type kind floor  (+ summary·distribution 만 min_coverage)
 *   geo_*     (지도)        busi zcode maker chger_type kind floor  — 기간·연식 없음
 *   cohort_survival_input   busi maker 만
 *   scorecard_*             없음 (사업자 하나를 URL 로 고른다)
 *   graph_*   (분석)        없음 (그래프는 빌드 산출물 전체를 그린다)
 *   quality_* (품질)        없음 (수집 자체의 품질이라 모집단 필터가 의미 없다)
 *   reconcile_*             없음 (전국 공표통계와의 대조라 부분집합이면 비교가 깨진다)
 */
export const PAGE_SCOPE: Record<string, Scope> = {
  overview: {
    applies: ALL,
    offKind: 'n/a',
    // queries.sql 실측: $min_coverage 를 받는 것은 metrics_summary·metrics_distribution (과 각 _all) 이다
    partial: { minCoverage: '요약 KPI·분포에 걸립니다 — 순위는 미달 대상을 분리해 표 보기에 둡니다' },
    reason: '',
  },
  usage: {
    applies: ALL,
    offKind: 'n/a',
    partial: { minCoverage: '요약 KPI 에만 걸립니다 — 순위·분포는 자체 기준으로 분리 표시' },
    reason: '',
  },
  aging: {
    // 노후화는 metrics_ranking 하나만 쓴다. 그 질의는 $min_coverage 를 받지
    // 않으므로 커버리지 축은 **걸리지 않는다** — 코호트별 커버리지는 표에
    // 그대로 실어 근거의 두께를 직접 보게 한다.
    applies: ['busi', 'zcode', 'maker', 'chgerType', 'kind', 'year', 'floor'],
    offKind: 'unsupported',
    offBy: {
      minCoverage: {
        kind: 'unsupported',
        reason: '순위 질의가 커버리지 축을 받지 않습니다 — 대신 코호트별 커버리지를 표에 싣습니다',
      },
    },
    reason:
      '연식 코호트는 순위 질의 하나로 그린다 — 커버리지 축은 타지 않고, 대신 코호트별 커버리지를 표에 함께 싣는다',
  },
  deterioration: {
    // 모든 값이 빌드·환경공단 집계가 사전 계산한 전국 값이다 (§8.11.4 필터).
    // 층 통제는 모집단 전체의 구조를 써야 성립하고, 환경공단 원천은 환경부 직접설치분뿐이라
    // 사업자·지역 축과 무관하다 — 걸면 질문이 성립하지 않으므로 '해당 없음'이다.
    applies: [],
    offKind: 'n/a',
    reason:
      '노후화 분석은 전국 구조로 통제하는 비교라 부분집합을 만들면 통제가 깨진다 — 환경공단 원천은 환경부 직접설치분뿐이다',
  },
  places: {
    applies: [],
    // **원칙이 아니라 제약이다.** 프로파일이 사전집계 산출물이라 못 거는 것이지,
    // 걸면 값이 틀려지는 종류가 아니다. 하려면 요청 시 구간 2,232만 행을 훑어야
    // 하는데 그것은 §7.3 위반이다 — 만들려면 집계 구조를 바꿔야 한다.
    offKind: 'unsupported',
    reason:
      '장소 프로파일은 전국 모집단 기준 사전집계다 — 부분집합을 만들면 개소당 대수와 구성비의 분모가 무너진다',
  },
  map: {
    // 연식 축은 v0.4 에서 geo_* 에 추가했다 — "어느 연식이 어디에 있나" (§8.10.2)
    applies: ['busi', 'zcode', 'maker', 'chgerType', 'kind', 'year', 'floor'],
    offKind: 'unsupported',
    reason: '지도는 현재 빌드의 최신 상태를 그린다 — 기간·커버리지 축은 타지 않는다',
  },
  cohort: {
    applies: ['busi', 'maker'],
    offKind: 'n/a',
    offBy: {
      minCoverage: {
        kind: 'unsupported',
        reason: '생존곡선 입력 질의가 커버리지 축을 받지 않습니다',
      },
    },
    reason: '생존곡선은 고장 세션 단위라 설비 속성 축(지역·타입·환경·층)으로 나누지 않는다',
  },
  scorecard: {
    applies: [],
    offKind: 'n/a',
    reason: '사업자 하나를 직접 골라 동급과 견주는 화면이라 전역 필터를 타지 않는다',
  },
  analysis: {
    applies: [],
    offKind: 'n/a',
    reason: '관계 그래프는 빌드가 만든 전체 구조를 그린다 — 부분집합이면 이웃 관계가 끊긴다',
  },
  reconcile: {
    applies: [],
    offKind: 'n/a',
    reason: '전국 공표통계와의 대조라 모집단을 좁히면 비교 자체가 성립하지 않는다',
  },
  quality: {
    applies: [],
    offKind: 'n/a',
    reason: '수집 과정 자체의 품질이라 설비 속성으로 거르지 않는다',
  },
}

export function scopeOf(page: string): Scope {
  return PAGE_SCOPE[page] ?? { applies: ALL, offKind: 'n/a', reason: '' }
}

export const OFF_LABEL: Record<OffKind, string> = {
  'n/a': '해당 없음',
  unsupported: '미지원',
}

/** 그 축이 왜 안 걸리는가 — 성격과 사유. 걸리는 축이면 `null` */
export function offInfo(page: string, axis: ScopeAxis): { kind: OffKind; reason: string } | null {
  const s = scopeOf(page)
  if (s.applies.includes(axis)) return null
  const own = s.offBy?.[axis]
  if (own) return own
  return { kind: s.offKind, reason: s.reason }
}

export function ignoredAxes(page: string): ScopeAxis[] {
  const s = scopeOf(page)
  return ALL.filter((a) => !s.applies.includes(a))
}

/** 이 화면에서 그 축이 어떤 상태인가 (DESIGN §8.6.10) */
export type AxisState = 'on' | 'partial' | 'off'

export function axisState(page: string, axis: ScopeAxis): AxisState {
  const s = scopeOf(page)
  if (!s.applies.includes(axis)) return 'off'
  return s.partial?.[axis] ? 'partial' : 'on'
}

/** 부분 적용 사유. 없으면 빈 문자열 */
export function partialReason(page: string, axis: ScopeAxis): string {
  return scopeOf(page).partial?.[axis] ?? ''
}
